import React, { useCallback, useRef } from 'react';
import { KeiAiModels } from '@common/model/admin/thirdPartyApi';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { Box } from '@cp/ds/src/components/box';
import { Button } from '@cp/ds/src/components/button';
import { Card } from '@cp/ds/src/components/card';
import { MODAL_MOBILE_BREAKPOINT_THRESHOLD, ModalLayout, ModalLayoutTokens } from '@cp/ds/src/components/modalLayout';
import { PhotoPicker } from '@cp/ds/src/components/photoPicker';
import { PhotoPickerHandle } from '@cp/ds/src/components/photoPicker/model';
import { Select, SelectItemModel } from '@cp/ds/src/components/select';
import { Stack } from '@cp/ds/src/components/stack';
import { Typography } from '@cp/ds/src/components/typography';
import { useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { ArrowRightShortIcon } from '@cp/ds/src/icons';
import { WithCommonModalProps } from 'src/app/shared/components/modals/model';
import { AiImageGeneratorModalProps } from 'src/app/shared/components/modals/aiImageGenerator/model';
import { useResumeData } from 'src/entities/resume';
import { thirdPartyApiApi } from '../api/thirdPartyApi';
import { AI_IMAGE_GENERATOR_CONSTANTS } from '../lib';
import { useImageGeneration } from '../hooks/useImageGeneration';
import { ImagePurpose } from '../types';
import { GenerationColumn } from './GenerationColumn';

const imageModels: SelectItemModel<string>[] = Object.values(KeiAiModels).map((model) => ({
  label: model,
  value: model,
}));

const AiImageGeneratorModal: React.FC<WithCommonModalProps<AiImageGeneratorModalProps>> = ({ passedProps, ...restProps }) => {
  const { onClose } = restProps;
  const { clientId, resumeId, originalImageUrl, onPhotoSaved } = passedProps;
  const sourcePhotoPickerRef = useRef<PhotoPickerHandle>(null);
  const resolver = useClientWebResolver();
  const { resume } = useResumeData({ resumeId });

  const { data: apiSettings } = thirdPartyApiApi.useGetApiSettingsQuery();
  const [createApiSettings, { isLoading: isSavingModel }] = thirdPartyApiApi.useCreateApiSettingsMutation();

  const {
    formValues,
    setValue,
    setSourceImage,
    sourceError,
    isSaving,
    prompts,
    resumeState,
    avatarState,
    handlePromptChange,
    handleGenerate,
    handleSavePhoto,
    handleResultImageChange,
    dismissStatus,
  } = useImageGeneration({ clientId, resumeId, originalImageUrl, onPhotoSaved });

  const getSourceCroppedImage = async (): Promise<string | undefined> => {
    try {
      return await sourcePhotoPickerRef.current?.getCroppedImage({
        maxWidth: AI_IMAGE_GENERATOR_CONSTANTS.GENERATE_IMAGE_MAX_SIZE,
        maxHeight: AI_IMAGE_GENERATOR_CONSTANTS.GENERATE_IMAGE_MAX_SIZE,
        quality: AI_IMAGE_GENERATOR_CONSTANTS.GENERATE_IMAGE_JPEG_QUALITY,
      });
    } catch {
      return undefined;
    }
  };

  const handleGenerateClick = async (purpose: ImagePurpose) => {
    const cropped = await getSourceCroppedImage();
    await handleGenerate(purpose, cropped);
  };

  const handleModelChange = useCallback(
    async (model: string) => {
      if (!apiSettings) {
        return;
      }

      await createApiSettings({
        apiKey: apiSettings.apiKey,
        model,
        textModel: apiSettings.textModel,
        url: apiSettings.url,
      }).unwrap();
    },
    [apiSettings, createApiSettings],
  );

  const handleOpenCatalog = useCallback(() => {
    if (!resume) {
      return;
    }

    const url = resolver.resolve({
      page: ClientWebPage.Catalog,
      pageProps: { catalogType: resume.type, careerId: resume.careerId },
      cityAlias: resume.city?.alias,
    });

    window.open(String(url), '_blank', 'noopener,noreferrer');
  }, [resume, resolver]);

  return (
    <ModalLayout
      containerSx={(theme) => ({
        width: AI_IMAGE_GENERATOR_CONSTANTS.MODAL_SIZE,
        maxWidth: AI_IMAGE_GENERATOR_CONSTANTS.MODAL_SIZE,
        height: AI_IMAGE_GENERATOR_CONSTANTS.MODAL_HEIGHT,
        maxHeight: AI_IMAGE_GENERATOR_CONSTANTS.MODAL_HEIGHT,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        overscrollBehavior: 'none',
        boxSizing: 'border-box',
        [ModalLayoutTokens.PaddingTop]: 0,
        [ModalLayoutTokens.PaddingBottom]: 0,
        [ModalLayoutTokens.PaddingX]: 0,
        padding: 0,
        paddingTop: 0,
        paddingBottom: 0,
        paddingInline: 0,
        [theme.breakpoints.up(MODAL_MOBILE_BREAKPOINT_THRESHOLD)]: {
          [ModalLayoutTokens.PaddingTop]: 0,
          [ModalLayoutTokens.PaddingBottom]: 0,
          [ModalLayoutTokens.PaddingX]: 0,
          padding: 0,
          paddingTop: 0,
          paddingBottom: 0,
          paddingInline: 0,
        },
      })}
      onClose={onClose}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: 1.5,
          boxSizing: 'border-box',
        }}
      >
        {sourceError && (
          <Box sx={{ mb: 1.5, p: 1.5, flexShrink: 0 }}>
            <Typography color="error">{sourceError}</Typography>
          </Box>
        )}

        <Stack
          alignItems="stretch"
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ flex: 1, minHeight: 0, minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}
        >
          <Stack
            justifyContent="space-between"
            spacing={2}
            sx={{
              width: { md: AI_IMAGE_GENERATOR_CONSTANTS.SOURCE_CARD_WIDTH },
              minWidth: { md: AI_IMAGE_GENERATOR_CONSTANTS.SOURCE_CARD_WIDTH },
              maxWidth: { md: AI_IMAGE_GENERATOR_CONSTANTS.SOURCE_CARD_WIDTH },
              height: { md: '100%' },
              flex: '0 0 auto',
              minHeight: 0,
            }}
          >
            <Card
              sx={{
                p: { xs: 2, md: 2.5 },
                width: '100%',
                height: AI_IMAGE_GENERATOR_CONSTANTS.SOURCE_CARD_HEIGHT,
                minHeight: AI_IMAGE_GENERATOR_CONSTANTS.SOURCE_CARD_HEIGHT,
                maxHeight: AI_IMAGE_GENERATOR_CONSTANTS.SOURCE_CARD_HEIGHT,
                flex: '0 0 auto',
                boxSizing: 'border-box',
                backgroundColor: AI_IMAGE_GENERATOR_CONSTANTS.COLUMN_BACKGROUND,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Typography sx={{ mb: 1.5, flexShrink: 0 }} variant="h6">
                Исходное изображение
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  width: AI_IMAGE_GENERATOR_CONSTANTS.SOURCE_CANVAS_SIZE,
                  mx: 'auto',
                  flexShrink: 0,
                }}
              >
                <PhotoPicker
                  actionBarPosition="bottom"
                  canvasSize={AI_IMAGE_GENERATOR_CONSTANTS.SOURCE_CANVAS_SIZE}
                  disabled={formValues.isConverting}
                  freeScale
                  imageUrl={formValues.selectedImageUrl || undefined}
                  onChangeImageUrl={setSourceImage}
                  onLoadingStateChange={(loading) => setValue('isConverting', loading)}
                  ref={sourcePhotoPickerRef}
                  showDownloadButton
                />
              </Box>
            </Card>

            <Stack alignItems="flex-end" direction="row" spacing={2} sx={{ flexShrink: 0, width: '100%' }}>
              <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Typography color="text.secondary" variant="caption">
                  Модель
                </Typography>
                <Select
                  disabled={!apiSettings || isSavingModel}
                  items={imageModels}
                  onChange={handleModelChange}
                  placeholder="Выберите модель"
                  size="small"
                  value={apiSettings?.model}
                />
              </Stack>
              <Button
                disabled={!resume}
                iconRight={<ArrowRightShortIcon />}
                onClick={handleOpenCatalog}
                size="small"
                sx={{ flexShrink: 0 }}
                variant="secondary"
              >
                Каталог
              </Button>
            </Stack>
          </Stack>

          <GenerationColumn
            disabled={!formValues.selectedImageUrl || formValues.isConverting}
            isSaving={isSaving}
            onDismissStatus={(statusId) => dismissStatus('avatar', statusId)}
            onGenerate={() => handleGenerateClick('avatar')}
            onPromptChange={(key, value) => handlePromptChange('avatar', key, value)}
            onResultImageChange={(resultId, url) => handleResultImageChange('avatar', resultId, url)}
            onSaveResult={(resultId, savePurpose, croppedDataUrl) =>
              handleSavePhoto({ columnPurpose: 'avatar', resultId, savePurpose, croppedDataUrl })
            }
            prompts={prompts}
            purpose="avatar"
            state={avatarState}
            title="Фото для аватара"
          />

          <GenerationColumn
            disabled={!formValues.selectedImageUrl || formValues.isConverting}
            isSaving={isSaving}
            onDismissStatus={(statusId) => dismissStatus('resume', statusId)}
            onGenerate={() => handleGenerateClick('resume')}
            onPromptChange={(key, value) => handlePromptChange('resume', key, value)}
            onResultImageChange={(resultId, url) => handleResultImageChange('resume', resultId, url)}
            onSaveResult={(resultId, savePurpose, croppedDataUrl) =>
              handleSavePhoto({ columnPurpose: 'resume', resultId, savePurpose, croppedDataUrl })
            }
            prompts={prompts}
            purpose="resume"
            state={resumeState}
            title="Фото для анкеты"
          />
        </Stack>
      </Box>
    </ModalLayout>
  );
};

export default AiImageGeneratorModal;
